import React from "react";
import { Container } from "../../common/HtmlTags/Container/Container";
import { Button } from "../../common/HtmlTags/Button/Button";
import { InputField } from "../../common/HtmlTags/InputField/InputField";
import { ReactComponent as SearchIcon } from "../../../assets/images/search_icon.svg";
import "leaflet/dist/leaflet.css";
import "./MapLocationPicker.css";
import { useMapLocationPicker } from "./hooks/useMapLocationPicker";
import { MapLocationPickerProps } from "./types";

/**
 * MapLocationPicker component
 *
 * An interactive map component that allows users to select a location through
 * visual interaction or text search. Built on top of Leaflet.js with OpenStreetMap
 * tiles and integrated with Nominatim geocoding services.
 *
 * Features:
 * - Interactive map with click-to-select functionality
 * - Text-based location search with autocomplete suggestions
 * - Real-time marker placement and movement
 * - Reverse geocoding to convert coordinates to readable addresses
 * - Keyboard navigation support (Enter key for search)
 * - Loading states for search and geocoding operations
 * - Error handling with user-friendly fallback messages
 * - Responsive design with customizable styling
 *
 * Map Features:
 * - OpenStreetMap tile integration with proper attribution
 * - Clickable map for location selection
 * - Dynamic marker positioning and updates
 * - Zoom controls and pan functionality
 * - High-quality tile rendering with multiple zoom levels
 *
 * Search & Geocoding:
 * - Nominatim API integration for location search
 * - Reverse geocoding for address lookup
 * - Fallback to coordinate display when geocoding fails
 * - Debounced search requests to prevent API spam
 * - Error handling for network and API failures
 *
 * @param {MapLocationPickerProps} props - Component props
 * @param {Position} [props.initialPosition] - Initial position to center the map on
 * @param {(location: LocationWithAddress) => void} props.onSelect - Callback function triggered when location is selected
 *
 * @example
 * ```tsx
 * const [selectedLocation, setSelectedLocation] = useState<LocationWithAddress | null>(null);
 *
 * const handleLocationSelect = (location: LocationWithAddress) => {
 *   setSelectedLocation(location);
 *   console.log('Selected location:', location);
 * };
 *
 * return (
 *   <MapLocationPicker
 *     initialPosition={{ lat: 40.7128, lng: -74.0060 }}
 *     onSelect={handleLocationSelect}
 *   />
 * );
 *
 *
 * @returns {JSX.Element} Interactive map component with search and selection functionality
 */
export default function MapLocationPicker({
  initialPosition,
  onSelect,
}: MapLocationPickerProps) {
  const {
    mapRef,
    picked,
    isReverseGeocoding,
    searchQuery,
    setSearchQuery,
    isSearching,
    handleSearch,
    handleKeyPress,
    handleConfirm,
  } = useMapLocationPicker(initialPosition, onSelect);

  return (
    <Container className="map-location-picker-wrap">
      <Container className="map-search-section">
        <Container className="map-search-input-wrap">
          <InputField
            placeholder="Search for a location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            className="map-search-input"
            showSearchButton={true}
            buttonClick={handleSearch}
          />
        </Container>
        <Button
          type="button"
          onClick={handleSearch}
          disabled={isSearching || !searchQuery.trim()}
          palette="primary"
          size="small"
          colorScheme="light"
          hasIcon={false}
          className="map-search-btn"
        >
          <SearchIcon />
        </Button>
      </Container>
      <Container className="map-canvas" ref={mapRef} decorativeContainer />
      <Container className="map-actions">
        <Button
          type="button"
          onClick={handleConfirm}
          disabled={!picked || isReverseGeocoding}
          palette="primary"
          size="medium"
          colorScheme="dark"
          hasIcon={false}
        >
          {isReverseGeocoding ? "Selecting..." : "Use this location"}
        </Button>
      </Container>
    </Container>
  );
}
